/**
 * Colour grading (D-047 lighting overhaul; style guide §3.2): the tone-mapping operator, a generated 3D LUT and the
 * film-grain helper.
 *
 * Signal order (post/graph.ts): scene HDR → bloom → exposure → tone map (once) → sRGB encode → **LUT** → vignette →
 * grain → dither. The LUT is a **display-domain** grade: its input and output are sRGB-encoded display values in
 * [0, 1] (grid point i/(n-1) per channel); moving it before tone mapping would need a rebuild. It is generated at
 * load from post.json `grade` (zero download bytes): white balance and lift/gain in linear light, luma-preserving
 * split toning (cool nebula shadows, warm star highlights), saturation and vibrance, then a display-space contrast
 * S-curve around a pivot. The pure maths is tested.
 */
import { AgXToneMapping, ACESFilmicToneMapping, ClampToEdgeWrapping, Data3DTexture, LinearFilter, NeutralToneMapping, RGBAFormat, UnsignedByteType, type ToneMapping } from 'three/webgpu'

export const TONE_MAPPERS = { agx: AgXToneMapping, aces: ACESFilmicToneMapping, neutral: NeutralToneMapping } as const
export type ToneMapperName = keyof typeof TONE_MAPPERS

export function toneMappingOf(name: ToneMapperName): ToneMapping {
  return TONE_MAPPERS[name]
}

export interface GradeParams {
  /** Linear-light multipliers per channel (white balance / gain). */
  gain: readonly [number, number, number]
  /** Linear-light lift toward the shadow floor per channel (raises blacks, tints them). */
  lift: readonly [number, number, number]
  /** Split toning: linear RGB tint colours (luma-normalized) and amounts. */
  shadowTint: readonly [number, number, number]
  shadowAmount: number
  highlightTint: readonly [number, number, number]
  highlightAmount: number
  saturation: number
  /** Extra saturation for low-saturation pixels (0 = none). */
  vibrance: number
  /** Display-space S-curve slope at the pivot (1 = none). */
  contrast: number
  pivot: number
}

export const NEUTRAL_GRADE: GradeParams = {
  gain: [1, 1, 1],
  lift: [0, 0, 0],
  shadowTint: [1, 1, 1],
  shadowAmount: 0,
  highlightTint: [1, 1, 1],
  highlightAmount: 0,
  saturation: 1,
  vibrance: 0,
  contrast: 1,
  pivot: 0.45,
}

export function srgbToLinear(c: number): number {
  return c <= 0.04045 ? c / 12.92 : ((c + 0.055) / 1.055) ** 2.4
}

export function linearToSrgb(c: number): number {
  const x = Math.max(0, c)
  return x <= 0.0031308 ? x * 12.92 : 1.055 * x ** (1 / 2.4) - 0.055
}

function luma(r: number, g: number, b: number): number {
  return 0.2126 * r + 0.7152 * g + 0.0722 * b
}

function smoothstep(a: number, b: number, x: number): number {
  const t = Math.min(1, Math.max(0, (x - a) / (b - a)))
  return t * t * (3 - 2 * t)
}

/** Contrast S-curve on a display value: slope `k` at `pivot`, 0 and 1 fixed, monotonic. */
export function sCurve(x: number, k: number, pivot: number): number {
  const v = Math.min(1, Math.max(0, x))
  if (k === 1) return v
  return v < pivot ? pivot * (v / pivot) ** k : 1 - (1 - pivot) * ((1 - v) / (1 - pivot)) ** k
}

/** Grades one display-encoded colour into `out` (display-encoded, clamped to [0, 1]). */
export function gradePixel(r: number, g: number, b: number, p: GradeParams, out: Float64Array): Float64Array {
  let lr = srgbToLinear(r) * p.gain[0]
  let lg = srgbToLinear(g) * p.gain[1]
  let lb = srgbToLinear(b) * p.gain[2]
  lr += p.lift[0] * (1 - Math.min(lr, 1))
  lg += p.lift[1] * (1 - Math.min(lg, 1))
  lb += p.lift[2] * (1 - Math.min(lb, 1))
  // Split toning, luma-preserving: shadows lean toward the nebula, highlights toward the star.
  const l0 = Math.max(luma(lr, lg, lb), 1e-6)
  const t = smoothstep(0, 1, Math.sqrt(Math.min(l0, 1)))
  const ws = (1 - t) * (1 - t) * p.shadowAmount
  const wh = t * t * p.highlightAmount
  const ls = luma(p.shadowTint[0], p.shadowTint[1], p.shadowTint[2])
  const lh = luma(p.highlightTint[0], p.highlightTint[1], p.highlightTint[2])
  lr *= 1 + ws * (p.shadowTint[0] / ls - 1) + wh * (p.highlightTint[0] / lh - 1)
  lg *= 1 + ws * (p.shadowTint[1] / ls - 1) + wh * (p.highlightTint[1] / lh - 1)
  lb *= 1 + ws * (p.shadowTint[2] / ls - 1) + wh * (p.highlightTint[2] / lh - 1)
  // Saturation and vibrance around luma.
  const l = luma(lr, lg, lb)
  const mx = Math.max(lr, lg, lb)
  const mn = Math.min(lr, lg, lb)
  const sat = mx > 1e-6 ? (mx - mn) / mx : 0
  const s = p.saturation * (1 + p.vibrance * (1 - sat))
  lr = l + (lr - l) * s
  lg = l + (lg - l) * s
  lb = l + (lb - l) * s
  out[0] = sCurve(linearToSrgb(lr), p.contrast, p.pivot)
  out[1] = sCurve(linearToSrgb(lg), p.contrast, p.pivot)
  out[2] = sCurve(linearToSrgb(lb), p.contrast, p.pivot)
  return out
}

/** RGBA8 texels of an n³ LUT (r fastest, then g, then b: Data3DTexture order). */
export function buildGradeLut(n: number, p: GradeParams): Uint8Array {
  const data = new Uint8Array(n * n * n * 4)
  const px = new Float64Array(3)
  let o = 0
  for (let bi = 0; bi < n; bi++)
    for (let gi = 0; gi < n; gi++)
      for (let ri = 0; ri < n; ri++) {
        gradePixel(ri / (n - 1), gi / (n - 1), bi / (n - 1), p, px)
        data[o++] = Math.round(px[0] * 255)
        data[o++] = Math.round(px[1] * 255)
        data[o++] = Math.round(px[2] * 255)
        data[o++] = 255
      }
  return data
}

export function createGradeTexture(n: number, p: GradeParams): Data3DTexture {
  const tex = new Data3DTexture(buildGradeLut(n, p), n, n, n)
  tex.name = 'post.gradeLut'
  tex.format = RGBAFormat
  tex.type = UnsignedByteType
  tex.minFilter = LinearFilter
  tex.magFilter = LinearFilter
  tex.wrapS = ClampToEdgeWrapping
  tex.wrapT = ClampToEdgeWrapping
  tex.wrapR = ClampToEdgeWrapping
  tex.generateMipmaps = false
  tex.unpackAlignment = 1
  tex.needsUpdate = true
  return tex
}

/** Rewrites an existing LUT in place (dev panel). */
export function updateGradeTexture(tex: Data3DTexture, n: number, p: GradeParams): void {
  const data = tex.image.data as Uint8Array
  data.set(buildGradeLut(n, p))
  tex.needsUpdate = true
}

/** post.json `grade` → GradeParams (hex tints to linear RGB). */
export function gradeParams(g: {
  gain: readonly [number, number, number]
  lift: readonly [number, number, number]
  shadowTint: string
  shadowAmount: number
  highlightTint: string
  highlightAmount: number
  saturation: number
  vibrance: number
  contrast: number
  pivot: number
}): GradeParams {
  return {
    gain: g.gain,
    lift: g.lift,
    shadowTint: hexToLinear(g.shadowTint),
    shadowAmount: g.shadowAmount,
    highlightTint: hexToLinear(g.highlightTint),
    highlightAmount: g.highlightAmount,
    saturation: g.saturation,
    vibrance: g.vibrance,
    contrast: g.contrast,
    pivot: g.pivot,
  }
}

/** An sRGB hex colour as linear RGB (tint colours in post.json are authored as hex). */
export function hexToLinear(hex: string): [number, number, number] {
  const n = Number.parseInt(hex.slice(1), 16)
  return [srgbToLinear(((n >> 16) & 255) / 255), srgbToLinear(((n >> 8) & 255) / 255), srgbToLinear((n & 255) / 255)]
}
